/*
 * Shared by the revenue analysis pages (Edutech / Publication / Shopify split):
 *   CXOPanel/sourceWiseAnalysis.js  (more can reuse it: state-wise, payment-wise, …)
 * Business rules are the same as the old pages; only moved here.
 */
import { transactionTypeList } from "../../Utils/dataConstants";
import { formatINR } from "./transactionCommon";

export const TRANSACTION_TYPES = transactionTypeList; // 1 Edutech · 2 Publication · 3 Shopify
export const EDUTECH = 1;
export const PUBLICATION = 2;
export const SHOPIFY = 3;
export const SHOPIFY_PAYMENT_TYPE_ID = 5;

/** Payment types offered for each transaction type (unchanged rule) */
export const paymentTypesFor = (allTypes, typeId) =>
  (allTypes || []).filter((item) =>
    typeId === EDUTECH
      ? item?._id !== 5 && item?._id !== 6
      : typeId === PUBLICATION
        ? item?._id !== 3 && item?._id !== 5
        : typeId === SHOPIFY
          ? item?._id === SHOPIFY_PAYMENT_TYPE_ID
          : true,
  );

/** Shopify has one payment type and it is always selected (as before) */
export const defaultPaymentFor = (allTypes, typeId) =>
  typeId === SHOPIFY ? paymentTypesFor(allTypes, typeId)[0] || "" : "";

/** Name of the revenue shown for each type */
export const revenueLabelFor = (typeId) =>
  typeId === EDUTECH ? "Actual Revenue" : typeId === PUBLICATION ? "Publication Revenue" : "Shopify Revenue";

/** API rows → rows for one transaction type, highest total first */
export const rowsForType = (list, typeId) =>
  (list || [])
    .map((item) =>
      typeId === EDUTECH
        ? {
            _id: item?._id,
            publication: 0,
            shopifyRevenue: 0,
            edutech: item?.edutech,
            actualTotalAmount: item?.actualTotalAmount,
            totalGstAmount: item?.totalGstAmount,
            totalAmount: item?.edutech,
          }
        : typeId === PUBLICATION
          ? {
              _id: item?._id,
              publication: item?.publication,
              edutech: 0,
              shopifyRevenue: 0,
              actualTotalAmount: 0,
              totalGstAmount: 0,
              totalAmount: item?.publication,
            }
          : {
              _id: item?._id,
              publication: 0,
              edutech: 0,
              shopifyRevenue: item?.shopifyRevenue,
              actualTotalAmount: 0,
              totalGstAmount: 0,
              totalAmount: item?.shopifyRevenue,
            },
    )
    .sort((a, b) => (Number(b.totalAmount) || 0) - (Number(a.totalAmount) || 0));

const sum = (rows, key) => rows.reduce((acc, row) => acc + (Number(row?.[key]) || 0), 0);

/** Totals for the cards / popup (same sums as the old showGraph) */
export const revenueTotals = (rows) => ({
  actual: sum(rows, "actualTotalAmount"),
  gst: sum(rows, "totalGstAmount"),
  publication: sum(rows, "publication"),
  shopify: sum(rows, "shopifyRevenue"),
  total: sum(rows, "totalAmount"),
});

/** The revenue the page headlines: Edutech actual, Publication, or Shopify */
export const headlineRevenue = (totals, typeId) =>
  typeId === EDUTECH ? totals.actual : typeId === PUBLICATION ? totals.publication : totals.shopify;

/** A row's revenue for this type (what "Overall Contribution" divides) */
export const rowRevenue = (row, typeId) =>
  Number(typeId === EDUTECH ? row?.actualTotalAmount : typeId === PUBLICATION ? row?.publication : row?.shopifyRevenue) || 0;

/** Share of the headline revenue, in % (0 when there's nothing to divide) */
export const contributionPercent = (row, typeId, headline) => {
  const value = rowRevenue(row, typeId);
  return value && headline ? (value / headline) * 100 : 0;
};

/** Rows for the "Total Breakdown" popup (same lines as the old GST "View" dropdown) */
export const revenueBreakdownRows = (totals, typeId) => [
  { label: "Actual Price", value: formatINR(totals.actual) },
  { label: "GST", value: formatINR(totals.gst) },
  { label: "Shopify", value: formatINR(totals.shopify) },
  ...(typeId === PUBLICATION ? [{ label: "Publication", value: formatINR(totals.publication) }] : []),
  { label: "Total Price", value: formatINR(totals.total) },
];

/**
 * CanvasJS options — the same chart as before:
 * one column series for the type's revenue, plus stacked GST for Edutech.
 */
export const buildRevenueChartOptions = ({ rows: allRows, typeId, title, axisXTitle, skipZero = false }) => {
  const label = (row) => row?._id || "Unknown";
  // skipZero: leave out bars with no revenue (state-wise did this for Edutech / Publication)
  const rows =
    skipZero && typeId !== SHOPIFY ? allRows.filter((row) => rowRevenue(row, typeId) !== 0) : allRows;
  const revenuePoints = rows.map((row) => ({ label: label(row), y: rowRevenue(row, typeId) }));
  return {
    animationEnabled: true,
    exportEnabled: false,
    title: { text: title, fontFamily: "verdana" },
    axisY: { title: "Revenue" },
    axisX: { title: axisXTitle, interval: 1 },
    toolTip: { shared: true, reversed: true },
    data: [
      { type: "column", name: revenueLabelFor(typeId), indexLabel: "{y}", showInLegend: true, dataPoints: revenuePoints },
      typeId === EDUTECH
        ? {
            type: "stackedColumn",
            name: "GST Amount",
            indexLabel: "{y}",
            showInLegend: true,
            dataPoints: rows.map((row) => ({ label: label(row), y: Number(row?.totalGstAmount) || 0 })),
          }
        : {},
    ],
  };
};
